import { createFileRoute } from "@tanstack/react-router";
import { buildMeta } from "@/lib/seo";
import { Fragment, useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { listBenzosPublic } from "@/lib/public-substances.functions";
import { WarningBanner, EmergencyBox, LegendFooter } from "@/components/BenzoWarnings";
import { useLang, useT } from "@/i18n/LanguageContext";
import { translateClass, translateVocab } from "@/i18n/strings";
import { PageSources } from "@/components/SourcesSheet";
import { errorMessage } from "@/lib/error-capture";

const benzoQuery = queryOptions({
  queryKey: ["public-benzos"],
  queryFn: () => listBenzosPublic(),
});

export const Route = createFileRoute("/benzo")({
  head: () =>
    buildMeta({
      title: "Benzo-Effekte-Vergleich",
      description:
        "Vergleichstabelle von Benzodiazepinen und Z-Drugs: Äquivalenzdosen, Wirkprofile, Halbwertszeit und Abhängigkeitsrisiko.",
      path: "/benzo",
    }),
  loader: ({ context }) => context.queryClient.ensureQueryData(benzoQuery),
  errorComponent: ({ error }) => <div role="alert" style={{ padding: "2rem", color: "#f87171" }}>Fehler: {errorMessage(error)}</div>,
  notFoundComponent: () => <div style={{ padding: "2rem" }}>Keine Benzo-Daten gefunden.</div>,
  component: BenzoPage,
});

type Compound = {
  Substance: string;
  Class: string;
  "Equiv. to Diazepam 10mg": string;
  "Typical Dose (mg)": string;
  "Max Daily (mg)": string;
  Anxiolytic: number;
  "Sedative/ Hypnotic": number;
  "Muscle Relaxant": number;
  Anticonvulsant: number;
  "Dependence Risk": number;
  Notes: string;
  Notes_de?: string;
  pharma?: Record<string, string>;
  warning?: boolean;
  nonComparable?: boolean;
};


function DotRating({ value, max = 5, color }: { value: number; max?: number; color: string }) {
  return (
    <span style={{ letterSpacing: "0.1em", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
      {Array.from({ length: max }).map((_, i) => (
        <span key={i} style={{ color: i < value ? color : "var(--text-muted)" }}>
          {i < value ? "●" : "○"}
        </span>
      ))}
    </span>
  );
}

const TH: React.CSSProperties = {
  background: "rgba(168,85,247,0.1)",
  color: "var(--text-secondary)",
  fontSize: "0.8rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  padding: "0.6rem 0.75rem",
  textAlign: "left",
  borderBottom: "1px solid var(--border-purple)",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const TD: React.CSSProperties = {
  padding: "0.5rem 0.75rem",
  fontSize: "0.85rem",
  color: "var(--text-primary)",
  borderBottom: "1px solid rgba(168,85,247,0.08)",
};

function BenzoPage() {
  const t = useT();
  const { lang } = useLang();
  const { data: compounds } = useSuspenseQuery(benzoQuery) as { data: Compound[] };
  const ALL = "__ALL__";
  const classes = useMemo<string[]>(() => [ALL, ...Array.from(new Set(compounds.map((c) => c.Class)))], [compounds]);
  const [cls, setCls] = useState<string>(ALL);
  const [expanded, setExpanded] = useState<string | null>(null);
  type SortKey = "Substance" | "Class" | "Equiv. to Diazepam 10mg" | "Half-Life";
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" } | null>(null);

  const extractNum = (s: string | undefined | null): number | null => {
    if (s == null) return null;
    const m = String(s).match(/-?\d+(?:\.\d+)?/);
    return m ? parseFloat(m[0]) : null;
  };

  const filtered = cls === ALL ? compounds : compounds.filter((c) => c.Class === cls);
  const rows = useMemo(() => {
    if (!sort) return filtered;
    const dir = sort.dir === "asc" ? 1 : -1;
    const indexed = filtered.map((c, i) => ({ c, i }));
    indexed.sort((a, b) => {
      let cmp = 0;
      if (sort.key === "Substance") {
        cmp = a.c.Substance.localeCompare(b.c.Substance, "de", { sensitivity: "base" });
      } else if (sort.key === "Class") {
        cmp = a.c.Class.localeCompare(b.c.Class);
      } else {
        const av = sort.key === "Half-Life"
          ? extractNum(a.c.pharma?.["Half-Life"])
          : extractNum(a.c[sort.key]);
        const bv = sort.key === "Half-Life"
          ? extractNum(b.c.pharma?.["Half-Life"])
          : extractNum(b.c[sort.key]);
        if (av === null && bv === null) cmp = 0;
        else if (av === null) return 1;
        else if (bv === null) return -1;
        else cmp = av - bv;
      }
      return cmp !== 0 ? cmp * dir : a.i - b.i;
    });
    return indexed.map((x) => x.c);
  }, [filtered, sort]);

  const toggleSort = (key: SortKey) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };

  const SortTH = ({ k, label }: { k: SortKey; label: string }) => {
    const active = sort?.key === k;
    const arrow = active ? (sort!.dir === "asc" ? "▲" : "▼") : "⇅";
    return (
      <th
        style={{ ...TH, cursor: "pointer", userSelect: "none", position: "sticky", top: 0, zIndex: 1 }}
        onClick={() => toggleSort(k)}
      >
        {label}{" "}
        <span style={{ color: active ? "var(--accent)" : "var(--text-muted)", fontSize: "0.75rem" }}>
          {arrow}
        </span>
      </th>
    );
  };

  const PHARMA_LABELS: Record<string, string> = {
    "Half-Life": t("benzo.pharma.halfLife"),
    "Active Metabolites": t("benzo.pharma.activeMetabolites"),
    "Onset of Action": t("benzo.pharma.onset"),
    "Peak Level": t("benzo.pharma.peak"),
    "Duration Category": t("benzo.pharma.duration"),
    "Lipophilicity": t("benzo.pharma.lipophilicity"),
    "Primary Metabolic Pathway": t("benzo.pharma.metabolism"),
    "Special Populations": t("benzo.pharma.special"),
  };

  const PHARMA_ORDER = [
    "Half-Life",
    "Active Metabolites",
    "Onset of Action",
    "Peak Level",
    "Duration Category",
    "Lipophilicity",
    "Primary Metabolic Pathway",
    "Special Populations",
  ];

  const pharmaValue = (c: Compound, key: string): string => {
    const pharma = c.pharma;
    if (!pharma) return "";
    if (key === "Special Populations") {
      const de = pharma["Special Populations_de"];
      const en = pharma["Special Populations"];
      return lang === "de" ? (de ?? en ?? "") : (en ?? "");
    }
    const raw = pharma[key];
    if (raw == null) return "";
    if (key === "Active Metabolites" || key === "Lipophilicity" || key === "Onset of Action" || key === "Duration Category") {
      return translateVocab(raw, lang);
    }
    return raw;
  };

  return (
    <div data-section="benzo">
      <h1 className="page-title">{t("benzo.title")}</h1>
      <div className="page-title-bar" />


      <WarningBanner />
      <EmergencyBox />


      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1.25rem" }}>
        {classes.map((c) => {
          const active = c === cls;
          const label = c === ALL ? t("benzo.filter.all") : translateClass(c, lang);
          return (
            <button
              key={c}
              onClick={() => setCls(c)}
              className={active ? "" : "glass-card"}
              style={{
                borderRadius: 6,
                padding: "0.35rem 0.8rem",
                fontSize: "0.8rem",
                cursor: "pointer",
                background: active ? "var(--accent)" : undefined,
                color: active ? "var(--ink-on-accent)" : "var(--text-muted)",
                border: active ? "1px solid var(--accent)" : undefined,

                fontWeight: active ? 600 : 400,
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div
        className="glass-card"
        style={{ overflowX: "auto", padding: 0, background: "var(--bg-surface)" }}
      >
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 560 }}>
          <thead>
            <tr>
              <SortTH k="Substance" label={t("benzo.col.substance")} />
              <SortTH k="Class" label={t("benzo.col.class")} />
              <SortTH k="Half-Life" label={t("benzo.col.halflife")} />
              <SortTH k="Equiv. to Diazepam 10mg" label={t("benzo.col.equiv")} />
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const open = expanded === c.Substance;
              const notesText = lang === "de" ? (c.Notes_de ?? c.Notes) : c.Notes;
              return (
                <Fragment key={c.Substance}>
                  <tr
                    onClick={() => setExpanded(open ? null : c.Substance)}
                    style={{ cursor: "pointer", background: open ? "rgba(255,255,255,0.03)" : undefined }}
                    onMouseEnter={(e) => {
                      if (!open) (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.03)";
                    }}
                    onMouseLeave={(e) => {
                      if (!open) (e.currentTarget as HTMLElement).style.background = "";
                    }}
                  >
                    <td style={{ ...TD, fontWeight: 600 }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
                        {c.warning && (
                          <AlertTriangle
                            size={14}
                            style={{ color: "#f87171", flexShrink: 0 }}
                            aria-label={t("benzo.warnIcon")}
                          />
                        )}
                        {c.Substance}
                      </span>
                    </td>
                    <td style={{ ...TD, color: "var(--text-secondary)" }}>{translateClass(c.Class, lang)}</td>
                    <td style={TD}>{c.pharma?.["Half-Life"] ?? "-"}</td>
                    <td style={TD}>{c["Equiv. to Diazepam 10mg"]}</td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={4} style={{ background: "rgba(26,16,51,0.9)", padding: "1rem 0.75rem" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                          <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", lineHeight: 1.5 }}>{notesText}</p>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "1.5rem", fontSize: "0.8rem" }}>
                            <div>
                              <span style={{ color: "var(--text-muted)" }}>{t("benzo.detail.typicalDose")}: </span>
                              <span style={{ color: "var(--text-primary)" }}>{c["Typical Dose (mg)"]}</span>
                            </div>
                            <div>
                              <span style={{ color: "var(--text-muted)" }}>{t("benzo.detail.maxDaily")}: </span>
                              <span style={{ color: "var(--text-primary)" }}>{c["Max Daily (mg)"]}</span>
                            </div>
                          </div>
                          <p style={{ color: "var(--text-muted)", fontSize: "0.75rem", lineHeight: 1.5, margin: 0 }}>
                            {t("benzo.detail.typicalDoseNote")}
                          </p>
                          <div style={{
                            borderTop: "1px solid var(--border-purple)",
                            paddingTop: "0.75rem",
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.4rem",
                          }}>
                            <span style={{ color: "var(--text-secondary)", fontSize: "0.8rem", fontWeight: 600 }}>
                              {t("benzo.detail.profileTitle")}
                            </span>
                            <div style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                              gap: "0.35rem 1.25rem",
                              fontSize: "0.8rem",
                              opacity: c.nonComparable ? 0.6 : 1,
                            }}>
                              {([
                                [t("benzo.col.anxiolytic"), c.Anxiolytic, "var(--accent-cyan)"],
                                [t("benzo.col.sedative"), c["Sedative/ Hypnotic"], "var(--accent-purple)"],
                                [t("benzo.col.relaxant"), c["Muscle Relaxant"], "#4ade80"],
                                [t("benzo.col.anticonvulsant"), c.Anticonvulsant, "#fb923c"],
                                [t("benzo.col.dependence"), c["Dependence Risk"], "#f87171"],
                              ] as Array<[string, number, string]>).map(([label, value, color]) => (
                                <div key={label} style={{ display: "flex", gap: "0.4rem", alignItems: "baseline", flexWrap: "wrap" }}>
                                  <span style={{ color: "var(--text-muted)" }}>{label}:</span>
                                  <DotRating value={value} color={color} />
                                  <span style={{ color: "var(--text-muted)", fontSize: "0.72rem" }}>{value}/5</span>
                                </div>
                              ))}
                            </div>
                            <p style={{ color: "var(--text-muted)", fontSize: "0.75rem", lineHeight: 1.5, margin: 0 }}>
                              {t("benzo.detail.profileNote")}
                              {c.nonComparable ? ` ${t("benzo.detail.notComparable")}` : ""}
                            </p>
                          </div>
                          {c.pharma && (
                            <div style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                              gap: "0.4rem 1.25rem",
                              borderTop: "1px solid var(--border-purple)",
                              paddingTop: "0.75rem",
                            }}>
                              {PHARMA_ORDER.filter((k) => c.pharma?.[k] != null).map((k) => (
                                <div key={k} style={{ fontSize: "0.8rem" }}>
                                  <span style={{ color: "var(--text-muted)" }}>{PHARMA_LABELS[k]}: </span>
                                  <span style={{ color: "var(--text-primary)" }}>{pharmaValue(c, k)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <LegendFooter />
      <PageSources path="/benzo" />
    </div>
  );
}
